import showdown from 'showdown';

import { EVENTS } from 'constants/events';
import { TYPE_SEND_EMAIL } from 'constants/ai';

import { isEmpty } from 'ravenjs/utils/lodash';

import { AI_DIGITAL_ASSISTANT_ENABLED } from 'constants/api';

export const getMarkdownConverter = () => {
    return new showdown.Converter({
        tables: true,
        strikethrough: true,
    });
};

export const clickReviewCompanyLink = sendPendoEvent => {
    const pendoEvent = EVENTS.AI_ASSISTANT.REVIEW_COMPANY_LOCATIONS;

    sendPendoEvent(pendoEvent);
};

export const clickAdditionalResource = (id, title, sendPendoEvent) => {
    const pendoEvent = EVENTS.AI_ASSISTANT.RESPONSE_LINK;
    pendoEvent.eventProperties.title = title;
    pendoEvent.eventProperties.responseID = id;

    sendPendoEvent(pendoEvent);
};

export const handlePendoAdditionalResources = (chat, sendPendoEvent) => {
    const pendoEvent = EVENTS.AI_ASSISTANT.RESPONSE_LINKS;
    pendoEvent.eventProperties.additionalResources = JSON.stringify(chat.additionalResources);
    pendoEvent.eventProperties.additionalResourcesFailed = JSON.stringify(
        chat.additionalResourcesFailed
    );

    sendPendoEvent(pendoEvent);
};

export const handlePendoAskButton = (message, sendPendoEvent) => {
    sendPendoEvent({
        event: EVENTS.AI_ASSISTANT.ASK_BUTTON.EVENT,
        eventProperties: {
            description: EVENTS.AI_ASSISTANT.ASK_BUTTON.EVENT_PROPERTIES.DESCRIPTION,
            message,
        },
    });
};

export const sendEmailSession = async params => {
    const { chatSessionId, sendEmailCloseAISession, userId, loggedCompanyId } = params;

    if (!chatSessionId) {
        return false;
    }

    try {
        await sendEmailCloseAISession({
            params: {
                type: TYPE_SEND_EMAIL,
                companyId: loggedCompanyId,
                additionalInfo: {
                    chatSessionId,
                },
                userIds: [userId],
            },
        });

        if (!isEmpty(JSON.parse(localStorage.getItem('ai_chat_session')))) {
            localStorage.removeItem('ai_chat_session');
        }
        return Promise.resolve(true);
    } catch (e) {
        return Promise.reject(e);
    }
};

export const clickDefaultPrompt = (title, sendPendoEvent) => {
    const pendoEvent = EVENTS.AI_ASSISTANT.DEFAULT_PROMPT;
    pendoEvent.eventProperties.title = title;

    sendPendoEvent(pendoEvent);
};

/**
 * Check env var to enable AI Maintenance
 *
 * @method isAiDigitalAssistantEnabled
 * @param {string} valueTest
 * @return {boolean}
 */
export const isAiDigitalAssistantEnabled = valueTest => {
    const envValue =
        valueTest !== undefined
            ? valueTest
            : typeof AI_DIGITAL_ASSISTANT_ENABLED !== 'undefined'
            ? AI_DIGITAL_ASSISTANT_ENABLED
            : 'true';
    return String(envValue).toLowerCase() !== 'false';
};

export const getCreateFeedbackParams = ({ data, userId, loggedCompanyId }) => {
    const {
        feedbackAnswer = '',
        feedbackMessage = '',
        chatSessionId,
        chatMessageId,
        isHelpful,
    } = data;

    return {
        chatMessageId,
        chatSessionId,
        clientId: loggedCompanyId,
        reason: feedbackAnswer,
        isHelpful,
        detail: feedbackMessage,
        userId,
    };
};

export const saveHistoryChatInLocalStorage = historyChat => {
    let historyChatResult = historyChat;
    const lastSelectedStateIndex = historyChat
        .map(item => item.selectedState?.value !== undefined)
        .lastIndexOf(true);

    if (lastSelectedStateIndex !== -1) {
        historyChatResult = historyChat.slice(lastSelectedStateIndex);
    }

    localStorage.setItem('ai_chat_session', JSON.stringify(historyChatResult));
};

export const createChatHistoryMessages = (response, message, selectedState) => {
    const { id: chatMessageId, answer = '', chatStatus, chatSessionId } = response;

    const userMessage = {
        id: `user-${chatMessageId}-${Date.now()}`,
        type: 'user',
        query: message,
    };

    const assistantMessage = {
        id: `msg-${chatMessageId}-${Date.now()}`,
        type: 'assistant',
        query: message,
        answer,
        chatSessionId,
        chatStatus,
        chatMessageId,
        additionalResources: [],
        additionalResourcesFailed: [],
        chatResources: {},
        verified: false,
        state: selectedState?.label || null,
        isAnimatingAnswer: true,
        startTime: Date.now(),
    };

    return { userMessage, assistantMessage };
};

export const updateHistoryChatWithFeedback = (historyChat, messageIndex, feedbackData) => {
    const updated = [...historyChat];
    updated[messageIndex] = {
        ...updated[messageIndex],
        ...feedbackData,
    };
    return updated;
};

export const updateHistoryChatWithLikeDislike = (historyChat, messageIndex, type) => {
    const updated = [...historyChat];
    updated[messageIndex] = {
        ...updated[messageIndex],
        likeDislike: type,
        waitingFeedback: true,
    };
    return updated;
};

export const cancelFeedback = (historyChat, messageIndex) => {
    const updated = [...historyChat];
    updated[messageIndex] = {
        ...updated[messageIndex],
        likeDislike: null,
        waitingFeedback: false,
    };
    return updated;
};

export const createStateChangeMessages = (previousState, newState, shouldAddEndConversation) => {
    const messages = [];

    if (shouldAddEndConversation) {
        messages.push({
            type: 'endConversation',
            previousSelectedState: previousState,
            id: `end-${Date.now()}`,
        });
    }

    messages.push({
        type: 'stateLabel',
        selectedState: newState,
        id: `state-${Date.now()}`,
    });

    return messages;
};

export const prepareSessionParams = ({
    selectedState,
    statesDataWithFederal,
    loggedCompanyId,
    userId,
    hasAdvisorSku,
}) => {
    const selectedStateData = selectedState ? selectedState.value : null;
    const stateData = statesDataWithFederal.find(state => state.stateCode === selectedStateData);
    const employeeCountData = stateData?.employeeCount || 0;
    const totalEmployeeCount = statesDataWithFederal.reduce(
        (sum, state) => sum + (state.employeeCount || 0),
        0
    );

    return {
        clientId: loggedCompanyId,
        employeeCount: employeeCountData,
        hasAdvisorSku,
        state: selectedStateData,
        userId,
        totalEmployeeCount,
    };
};

export const updateMessageWithAnswer = (historyChat, messageIndex, response, converter) => {
    const updated = [...historyChat];
    if (updated[messageIndex]) {
        updated[messageIndex].answer = converter.makeHtml(response.answer);
        updated[messageIndex].chatStatus = response.chatStatus;
        updated[messageIndex].resourceType = response.resourceType || null;
        updated[messageIndex].resources = response.resources || null;
        updated[messageIndex].chatResources = response.resources || {};
        updated[messageIndex].isAnimatingAnswer = true;

        const hasResources = !isEmpty(response.resources || null);
        updated[messageIndex].verified = hasResources;
    }
    return updated;
};

export const shouldFetchContentLinks = (response, statusComplete) => {
    return response.chatStatus === statusComplete && response.resourceType === 'contentful';
};

export const updateMessageAnimationComplete = (historyChat, messageIndex) => {
    const updated = [...historyChat];
    if (updated[messageIndex]) {
        updated[messageIndex].isAnimatingAnswer = false;
    }
    return updated;
};

export const calculateLoadingContentLinks = (message, contentLinkIds) => {
    if (!message || !contentLinkIds) return false;

    const loadedCount =
        message.additionalResources.length + message.additionalResourcesFailed.length;
    const totalCount = Object.keys(contentLinkIds).length;

    return loadedCount !== totalCount;
};

export const findLastSelectedState = parsedSession => {
    let selectedStateFromStorage = null;
    for (let i = parsedSession.length - 1; i >= 0; i -= 1) {
        if (parsedSession[i].selectedState) {
            selectedStateFromStorage = parsedSession[i].selectedState;
            break;
        }
    }
    return selectedStateFromStorage;
};

export const normalizeHistoryMessages = parsedSession => {
    return parsedSession.map((msg, index) => {
        // Skip state label messages
        if (msg.type === 'stateLabel' || msg.type === 'endConversation' || msg.selectedState) {
            return { ...msg, id: msg.id || `state-${index}` };
        }

        // Normalize assistant messages
        return {
            ...msg,
            id: msg.id || msg.chatMessageId || `msg-${index}`,
            additionalResources: Array.isArray(msg.additionalResources)
                ? msg.additionalResources
                : [],
            additionalResourcesFailed: Array.isArray(msg.additionalResourcesFailed)
                ? msg.additionalResourcesFailed
                : [],
            chatResources: msg.chatResources || {},
            resources: msg.resources || {},
            verified: !isEmpty(msg.additionalResources || []),
            isAnimatingAnswer: false,
        };
    });
};

export const sortStatesWithFederal = (states, federalOption) => {
    const sortedStates = [...states].sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
    });

    if (sortedStates.every(item => item.name !== 'Federal')) {
        return [federalOption, ...sortedStates];
    }
    return sortedStates;
};

export const mapStatesToOptions = statesWithFederal => {
    return statesWithFederal.map(state => {
        const { abbreviation, name } = state;
        return { value: abbreviation, label: name };
    });
};

export const extractCurrentChatTitle = historyChat => {
    // Find the last state label index
    const lastStateLabelIndex = historyChat
        .map((msg, index) => (msg.type === 'stateLabel' ? index : -1))
        .filter(index => index !== -1)
        .pop();

    // Get messages after the last state label (or all messages if no state label)
    const messagesAfterStateChange =
        lastStateLabelIndex !== undefined
            ? historyChat.slice(lastStateLabelIndex + 1)
            : historyChat;

    // Find the first user message in this subset
    const firstUserMessage = messagesAfterStateChange.find(msg => msg.type === 'user' && msg.query);
    return firstUserMessage ? firstUserMessage.query : null;
};

export const prepareFeedbackModalConfig = (message, handlers, defaultColors) => {
    return {
        title: 'Feedback',
        handlers,
        showTitle: true,
        description: '',
        itemData: message,
        justifyActions: 'flex-end',
        newModalStyles: true,
        ModalProps: {
            ContentProps: {
                style: {
                    maxWidth: '560px',
                    margin: '1rem',
                    padding: '2.5rem',
                    borderRadius: '0.5rem',
                    backgroundColor: defaultColors.WHITE,
                },
            },
        },
    };
};

export const shouldPollForAnswer = chatStatus => {
    const POLLING_STATUSES = ['OPEN', 'IN_PROCESS', 'PARTIAL_UPDATE'];
    return POLLING_STATUSES.includes(chatStatus);
};

export const isPollingComplete = chatStatus => {
    return chatStatus === 'COMPLETE' || chatStatus === 'ERROR';
};

export const preparePendoEventWithIds = (pendoEvent, chatMessageId, chatSessionId) => {
    if (chatMessageId && chatSessionId) {
        return {
            ...pendoEvent,
            eventProperties: {
                ...pendoEvent.eventProperties,
                chatMessageId,
                chatSessionId,
            },
        };
    }
    return pendoEvent;
};

export const getActiveChatId = (historyChat, getChatSessionIdFn) => {
    return historyChat.length > 0 ? getChatSessionIdFn() : null;
};

export const createHandlersObject = (
    fetchEmployeeData,
    clickReviewCompanyLinkFn,
    sendPendoEvent
) => {
    return {
        fetchEmployeeCountsByState: fetchEmployeeData,
        clickReviewCompanyLink: () => {
            clickReviewCompanyLinkFn(sendPendoEvent);
        },
    };
};

export const shouldCloseSession = previousStateValue => {
    return !isEmpty(previousStateValue);
};

export const calculateMessageIndex = currentHistoryChatLength => {
    return currentHistoryChatLength + 1;
};

export const loadAndNormalizeLocalStorageSession = () => {
    const localStorageSession = localStorage.getItem('ai_chat_session');
    if (isEmpty(localStorageSession)) {
        return null;
    }

    try {
        const parsedSession = JSON.parse(localStorageSession);
        if (parsedSession && parsedSession.length > 0) {
            return {
                selectedState: findLastSelectedState(parsedSession),
                normalizedHistory: normalizeHistoryMessages(parsedSession),
            };
        }
    } catch (error) {
        // Error parsing localStorage data
    }
    return null;
};

export const shouldFetchContentLinksForMessage = msg => {
    return (
        msg.resourceType === 'contentful' &&
        msg.chatStatus === 'COMPLETE' &&
        msg.resources &&
        typeof msg.resources === 'object' &&
        !Array.isArray(msg.resources) &&
        Object.keys(msg.resources).length > 0 &&
        Array.isArray(msg.additionalResources) &&
        msg.additionalResources.length === 0
    );
};

/**
 * Check if any messages in an array need to load content links (lazy loading)
 * Matches fetchInitContentLinks criteria exactly to avoid unnecessary calls
 * This is a standalone function that doesn't depend on other utility functions
 * to avoid issues with mocked functions in tests.
 *
 * @param {Array} messages - Array of message objects
 * @return {boolean} - True if any message needs content links loaded
 */
export const shouldLoadContentLinksForMessages = messages => {
    if (!messages || !Array.isArray(messages)) {
        return false;
    }

    return messages.some(msg => {
        if (!msg) return false;

        // Check if message has resources (must be non-empty object, not array)
        const hasResources =
            msg.resources &&
            typeof msg.resources === 'object' &&
            !Array.isArray(msg.resources) &&
            Object.keys(msg.resources).length > 0;

        // Check if additionalResources is empty array (needs loading)
        const needsLinks =
            Array.isArray(msg.additionalResources) && msg.additionalResources.length === 0;

        // Check if not already failed (avoid retries)
        const notFailed =
            !msg.additionalResourcesFailed || msg.additionalResourcesFailed.length === 0;

        // Check if message is complete
        const isComplete = msg.chatStatus === 'COMPLETE';

        // Check if contentful (must match fetchInitContentLinks criteria exactly)
        const isContentful = msg.resourceType === 'contentful';

        return hasResources && needsLinks && notFailed && isComplete && isContentful;
    });
};

export const groupMessagesForHistoryPure = (messages, sessionId) => {
    // Defensive: return empty array if messages is not valid
    if (!messages || !Array.isArray(messages)) {
        return [];
    }

    const groupedMessages = [];

    // Defensive: safely filter messages
    const filteredMessages = messages.filter(
        msg => msg && (msg.type === 'user' || msg.type === 'assistant')
    );

    for (let i = 0; i < filteredMessages.length; i += 1) {
        const msg = filteredMessages[i];

        if (msg.type === 'user') {
            const nextMsg = filteredMessages[i + 1];
            if (nextMsg && nextMsg.type === 'assistant') {
                // Skip message pairs that belong to a different session
                if (
                    sessionId &&
                    nextMsg.chatSessionId &&
                    String(nextMsg.chatSessionId) !== String(sessionId)
                ) {
                    i += 1;
                    continue; // eslint-disable-line no-continue
                }

                const messageData = {
                    query: msg.query || '',
                    answer: nextMsg.answer || '',
                    id: nextMsg.chatMessageId || nextMsg.id || `generated-${Date.now()}-${i}`,
                    chatSessionId: sessionId || 'unknown',
                    resources: Array.isArray(nextMsg.resources) ? nextMsg.resources : [],
                    additionalResources: Array.isArray(nextMsg.additionalResources)
                        ? nextMsg.additionalResources
                        : [],
                    additionalResourcesFailed: Array.isArray(nextMsg.additionalResourcesFailed)
                        ? nextMsg.additionalResourcesFailed
                        : [],
                    hasError: nextMsg.hasError || false,
                };

                // Add errorMessage separately if it exists and is a string
                if (nextMsg.errorMessage && typeof nextMsg.errorMessage === 'string') {
                    messageData.errorMessage = nextMsg.errorMessage;
                }

                groupedMessages.push(messageData);
                i += 1;
            } else {
                // User message without answer yet
                groupedMessages.push({
                    query: msg.query || '',
                    answer: '',
                    id: msg.chatMessageId || msg.id || `generated-${Date.now()}-${i}`,
                    chatSessionId: sessionId || 'unknown',
                    resources: [],
                    additionalResources: [],
                    additionalResourcesFailed: [],
                });
            }
        }
    }

    return groupedMessages;
};
